import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/*
 * Tooltip for app chrome -- the right rail, the header icons and the copilot
 * drawer header. Those used the browser's own `title`, which looks nothing like
 * the product and only appears after the OS delay (roughly a second), long
 * after the pointer has moved on.
 *
 * Portalled to <body> and positioned from the trigger's rect, so it is never
 * clipped by a scroll container and never trapped under the rail or the drawer
 * the way a nested absolute element would be.
 *
 * The host renders with `display: contents` so wrapping an icon adds no box and
 * cannot shift any existing layout; the rect is measured from the child.
 */
const SHOW_DELAY = 80;

const ShellTooltip = ({
  label = "",
  placement = "bottom",
  children,
  className = ""
}) => {
  const hostRef = useRef(null);
  const timerRef = useRef(null);
  const [pos, setPos] = useState(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clear();
    setPos(null);
  }, [clear]);

  const show = useCallback(() => {
    clear();
    timerRef.current = setTimeout(() => {
      const trigger = hostRef.current?.firstElementChild;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setPos(
        placement === "left"
          ? { top: rect.top + rect.height / 2, left: rect.left - 10 }
          : { top: rect.bottom + 8, left: rect.left + rect.width / 2 }
      );
    }, SHOW_DELAY);
  }, [clear, placement]);

  useEffect(() => clear, [clear]);

  return (
    <span
      ref={hostRef}
      className="sgTipHost"
      onMouseEnter={show}
      onMouseLeave={hide}
      // A tooltip that outlives the click it triggered reads as a stuck overlay.
      onMouseDown={hide}
    >
      {children}
      {pos &&
        label &&
        ReactDOM.createPortal(
          <span
            role="tooltip"
            className={`sgTip sgTip--${placement} ${className}`}
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {label}
          </span>,
          document.body
        )}
    </span>
  );
};

export default ShellTooltip;
export { ShellTooltip };
